import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'react-toastify';
import { Plus, Search, Building2, Edit, Mail, Phone, MapPin, Users } from 'lucide-react';
import { isSuperAdmin, isClinicAdmin } from '../utils/permissions';
import { useGetClinics, useGetClinicById, useAddClinic, useUpdateClinic, useStatusClinic } from '../hooks/useApi';
import { Switch } from '../components/ui/switch';
import { useDebounce } from '@/hooks/useDebounce';
const ClinicManagement = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const isAdmin = isSuperAdmin(currentUser) || isClinicAdmin(currentUser);
  
  // State declarations first
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingClinic, setEditingClinic] = useState(null);
  const [editingClinicId, setEditingClinicId] = useState(null);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [statusToggleData, setStatusToggleData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_number: '',
    email: '',
    doctor_name: '',
  });

  // Hooks that depend on state
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const { data: clinicsData, isLoading, refetch } = useGetClinics(debouncedSearchQuery, currentPage);
  

  const clinics = clinicsData?.data?.data || [];
  const from = clinicsData?.data?.from;
  const totalClinics = clinicsData?.data?.total || 0;
  
  const { mutate: addClinic, isPending: isAdding } = useAddClinic();
  const { mutate: updateClinic, isPending: isUpdating } = useUpdateClinic();
  const { mutate: toggleStatus, isPending: isToggling } = useStatusClinic();
  
  // Fetch clinic details when editing
  const { data: clinicDetails, isLoading: isLoadingClinicDetails } = useGetClinicById(editingClinicId, {
    enabled: !!editingClinicId && isDialogOpen,
  });

 

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (clinicsData?.data) {
       setTotalPages(clinicsData.data.last_page || 1);
    setCurrentPage(clinicsData.data.current_page || 1);
    }
  }, [clinicsData]);

  const getPageStyle = (page) => {
    const intensity = page / totalPages;
  
    if (page === currentPage) {
      return 'bg-primary-600 text-white scale-110';
    }
  
    if (intensity < 0.4) return 'bg-gray-100 text-gray-600';
    if (intensity < 0.7) return 'bg-gray-200 text-gray-700';
    return 'bg-gray-300 text-gray-800';
  };

  const handleOpenDialog = (clinic = null) => {
    if (clinic) {
      setEditingClinic(clinic);
      setEditingClinicId(clinic.id);
      // Set initial form data from the clinic object (will be updated when API data loads)
      setFormData({
        name: clinic.name || '',
        doctor_name: clinic.doctor_name || clinic.doctorName || '',
        address: clinic.address || '',
        contact_number: clinic.contact_number || clinic.phone || '',
        email: clinic.email || clinic.doctor_email || '',
      });
    } else {
      setEditingClinic(null);
      setEditingClinicId(null);
      setFormData({
        name: '',
        doctor_name: '',
        address: '',
        contact_number: '',
        email: '',
      });
    }
    setIsDialogOpen(true);
  };

  // Update form data when clinic details are fetched from API
  useEffect(() => {
    if (clinicDetails?.data && editingClinicId) {
      const clinic = clinicDetails.data;
      setFormData({
        name: clinic.name || '',
        doctor_name: clinic.doctor_name || clinic.doctorName || '',
        address: clinic.address || '',
        contact_number: clinic.contact_number || clinic.phone || '',
        email: clinic.email || clinic.doctor_email || '',
      });
    }
  }, [clinicDetails, editingClinicId]);

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setEditingClinic(null);
    setEditingClinicId(null);
    setFormData({
      name: '',
      address: '',
      contact_number: '',
      email: '',
      doctor_name: '',
    });
  };

const handleSubmit = () => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!formData.name || !formData.address || !formData.contact_number || !formData.email || !formData.doctor_name) {
    toast.error('Please fill in all required fields');
    return;
  }

  if (!emailRegex.test(formData.email)) {
    toast.error('Please enter a valid email address');
    return;
  }

  if (editingClinic) {
    updateClinic(
      { id: editingClinic.id, ...formData },
      {
        onSuccess: () => {
          toast.success(`${formData.name} has been updated successfully`);
          handleCloseDialog();
          refetch();
        },
        onError: (error) => {
          toast.error(error.response?.data?.message || 'Failed to update clinic');
        },
      }
    );
  } else {
    addClinic(formData, {
      onSuccess: () => {
        toast.success(`${formData.name} has been added successfully`);
        handleCloseDialog();
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to add clinic');
      },
    });
  }
};


  const handleStatusToggle = (clinicId, currentStatus) => {
    // Show confirmation dialog first
    setStatusToggleData({ clinicId, currentStatus });
    setIsStatusDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!statusToggleData) return;
    
    const { clinicId, currentStatus } = statusToggleData;
    toggleStatus(clinicId, {
      onSuccess: () => {
        toast.success(`Clinic status has been ${currentStatus ? 'deactivated' : 'activated'}`);
        refetch();
        setIsStatusDialogOpen(false);
        setStatusToggleData(null);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to update clinic status');
        setIsStatusDialogOpen(false);
        setStatusToggleData(null);
      },
    });
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
 
   

      {/* Search */}
      <Card >
        <CardContent className="p-6 flex flex-wrap gap-2 justify-between">
          <div className='flex gap-2'>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              placeholder="Search by name, phone, email, or doctor name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div>
            <Button
              onClick={() => handleOpenDialog()}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Clinic
            </Button>
          </div>
          </div>
          <div>
          <div>
            <p className="text-gray-600 mt-1 text-end">{totalClinics} clinic(s) found</p>
          </div>
          </div>
        </CardContent>
      </Card>

      {/* Clinic List Table */}
      {isLoading ? (
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">Loading clinics...</p>
            </div>
          </CardContent>
        </Card>
      ) : clinics.length === 0 ? (
        <Card>
          <CardContent className="p-12">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No clinics found</h3>
              <p className="text-gray-600 mb-6">Get started by adding your first clinic</p>
              <Button
                onClick={() => handleOpenDialog()}
                className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Clinic
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-y-auto max-h-[73vh]">
       
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">
                    <span>Sr. No.</span>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span className=''>Clinic Name</span>
                      <Building2 className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Doctor Name</TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span>Address</span>
                      <MapPin className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span>Phone</span>
                      <Phone className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className="text-center">
                    <div className="flex items-center justify-center gap-2">
                      <span>Email</span>
                      <Mail className="w-4 h-4 text-gray-400" />
                    </div>
                  </TableHead>
                  <TableHead className="text-center">Created</TableHead>
                  
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinics.map((clinic,index) => (
                  <TableRow key={clinic.id}>
                    <TableCell className="text-center">
                      <span className="text-sm">{from + index}</span>
                    </TableCell>
                    <TableCell className="font-medium text-center">
                      <div className="flex items-center justify-center gap-3">
                        {clinic.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-gray-600">{clinic.doctor_name || clinic.doctorName || 'N/A'}</span>
                    </TableCell>
                    <TableCell className="text-center w-[200px]">
                      <div className="flex items-center justify-center gap-2 max-w-xs mx-auto">
                        <span className="text-sm">{clinic.address}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm">{clinic.contact_number || clinic.phone || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <span className="text-sm">{clinic.doctor_email || clinic.email || 'N/A'}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm text-gray-600">{formatDate(clinic.created_at || clinic.createdAt)}</span>
                    </TableCell>
                    
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenDialog(clinic)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Switch
                          checked={clinic.status === true}
                          key={clinic.id}
                          onCheckedChange={() => handleStatusToggle(clinic.id, clinic.status)}
                          disabled={isToggling}
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
          
        </Card>
        
      )}
      {totalPages > 1 && (
          <div className="flex justify-center gap-2 mt-6">
  <Button
    variant="outline"
    disabled={currentPage === 1}
    onClick={() => setCurrentPage((p) => p - 1)}
  >
    Prev
  </Button>

  {Array.from({ length: totalPages }, (_, i) => {
    const page = i + 1;
    return (
      <button
        key={page}
        onClick={() => setCurrentPage(page)}
        className={`
          h-9 w-9 rounded-full transition-all
          ${getPageStyle(page)}
        `}
      >
        {page}
      </button>
    );
  })}

  <Button
    variant="outline"
    disabled={currentPage === totalPages}
    onClick={() => setCurrentPage((p) => p + 1)}
  >
    Next
  </Button>
</div>
          )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px] w-[95vw]">
          <DialogHeader>
            <DialogTitle>{editingClinic ? 'Edit Clinic' : 'Add New Clinic'}</DialogTitle>
            <DialogDescription>
              {editingClinic
                ? 'Update the clinic information below.'
                : 'Enter the details for the new clinic.'}
            </DialogDescription>
          </DialogHeader>
          {isLoadingClinicDetails && editingClinicId ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-3 text-gray-600">Loading clinic details...</span>
            </div>
          ) : (
          <div className="space-y-4 ">
            <div className="space-y-2">
              <Label htmlFor="name">Clinic Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder="Enter clinic name"
                onKeyPress={(e)=>{
                  if (!/^[a-zA-Z0-9 ]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="doctor_name">Doctor Name <span className="text-red-500">*</span></Label>
              <Input
                id="doctor_name"
                placeholder="Enter doctor name"
                value={formData.doctor_name}
                onKeyPress={(e)=>{
                  if (!/^[a-zA-Z ]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => handleInputChange('doctor_name', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address <span className="text-red-500">*</span></Label>
              <Input
                id="address"
                placeholder="Enter clinic address"
                value={formData.address}
                onKeyPress={(e)=>{
                  if (!/^[a-zA-Z0-9,./\- ]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}  
                onChange={(e) => handleInputChange('address', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_number">Phone <span className="text-red-500">*</span></Label>
              <Input
                id="contact_number"
                placeholder="9999999999"
                maxLength={10}
                type="tel"
                value={formData.contact_number}
                onKeyPress={(e) => {
                  if (!/^\d*$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => handleInputChange('contact_number', e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email <span className="text-red-500">*</span></Label>
              <Input
                id="email"
                type="email"
                placeholder="clinic@example.com"
                onKeyPress={(e) => {
                  if (!/^[a-zA-Z0-9@._-]$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
              />
            </div>
           
          </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {editingClinic ? 'Update' : 'Add'} Clinic
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Status Change</DialogTitle>
            <DialogDescription>
              Are you sure you want to {statusToggleData?.currentStatus ? 'deactivate' : 'activate'} this clinic?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="">
            <Button variant="outline"  onClick={() => {
              setIsStatusDialogOpen(false);
              setStatusToggleData(null);
            }}>
              Cancel
            </Button>
            <Button
              onClick={confirmStatusToggle}
              disabled={isToggling}
              className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
            >
              {isToggling ? 'Updating...' : 'Confirm'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClinicManagement;

